import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { join } from "path";
import { useCallback, useEffect, useState } from "react";

import JsonLd from "~/components/common/json-ld";
import EditorialEntry from "~/components/editorials/editorial-entry";
import LayoutPage from "~/components/layout/layout-page";
import ProductReviews from "~/components/product/product-reviews/product-reviews";
import ProductShowcase from "~/components/product/product-showcase";
import { useConfiguration } from "~/contexts/configuration";
import { useDeferredTracking, usePageTracking } from "~/contexts/page-trackings";
import Logger from "~/services/logger/logger";
import styles from "~/styles/pages/product.module.scss";
import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function ProductPage(props: Props) {
  const { eva } = useConfiguration();
  const router = useRouter();
  const [path, setPath] = useState(router.asPath);
  const [selected, setSelected] = useState(props.selected);
  const lastProductCategory = props.categories.at(-1)?.text;
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);
  const reviews = props.reviews;

  const getAvailability = useCallback(
    async (product: CrossCountry.DetailedProduct) => {
      setIsLoadingAvailability(true);
      const currentSelected = { ...product };
      await eva
        .getProductAvailabilityDeliveryOnly([{ ID: currentSelected.product_id }])
        .then((selectedAvailability) => {
          currentSelected.is_available = selectedAvailability?.Products?.[0].Delivery?.IsAvailable || false;
          currentSelected.has_stock = selectedAvailability?.Products?.[0].Delivery?.HasStock || false;
        })
        .catch((error) => Logger.instance.error("product detail availability KO", error))
        .finally(() => {
          setSelected(currentSelected);
          setIsLoadingAvailability(false);
        });
    },
    [eva]
  );

  useEffect(() => {
    getAvailability(props.selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when shallow navigating PDP (between shades) manually get data
  useEffect(() => {
    setPath(router.asPath);

    if (router.asPath !== path) {
      const currentProductId = (router.query.slug as string).split("-").at(-1)!;
      // not shallow routing
      if (currentProductId == props.selected.product_id) {
        getAvailability(props.selected);
      } else {
        eva
          .getProductDetail(currentProductId)
          .then(async (productDetail) => {
            if (!productDetail.Result) {
              router.push(router.asPath);
              return;
            }
            const selected = productDetail.Result;
            if (props.children.length > 0) {
              if (selected.is_available === undefined) {
                setIsLoadingAvailability(true);
                const selectedAvailability = await eva.getProductAvailabilityDeliveryOnly([
                  { ID: selected.product_id as string },
                ]);

                selected.is_available = selectedAvailability?.Products?.[0].Delivery?.IsAvailable || false;
                selected.has_stock = selectedAvailability?.Products?.[0].Delivery?.HasStock || false;
                setIsLoadingAvailability(false);
              }
            }

            setSelected(selected as any);
          })
          .catch((error) => Logger.instance.error("product detail shallow routing KO", error));
      }
    }
  }, [
    eva,
    getAvailability,
    path,
    props.children.length,
    props.organizationUnitId,
    props.selected,
    props.selected.product_id,
    router,
    router.locale,
    router.query.slug,
    selected,
  ]);

  usePageTracking("product", { ...TrackingUtils.mapTrackProductPage(props), freezeOn: props.root.backend_id });
  useDeferredTracking(
    TrackingUtils.mapEcommerceEvent("view_item", [TrackingUtils.mapEcomItem(props.root, selected)], {
      user_action: "detail",
    })
  );

  const reviewsResults = reviews?.results?.[0];
  const reviewsRollup = reviewsResults?.rollup;
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: selected.display_value,
    image: selected.primary_image?.url,
    description: selected.short_description,
    brand: {
      "@type": "Brand",
      name: Constants.BRAND_NAME,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: reviewsRollup?.average_rating,
      ratingCount: reviewsRollup?.review_count,
    },
    offers: {
      "@type": "Offer",
      price: selected.display_price,
      priceCurrency: selected.currency_id,
      availability: selected.has_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
  };

  return (
    <LayoutPage
      {...props}
      mainClasses={styles.productPage}
      productChildren={props.children}
      selected={selected}
      lastProductCategory={lastProductCategory}
      inPpd
    >
      <>
        <JsonLd data={JSON.stringify(productStructuredData)} />
        <ProductShowcase
          root={props.root}
          shades={props.children}
          selectedShade={selected}
          breadcrumbs={props.categories}
          isLoadingAvailability={isLoadingAvailability}
          parent_backend_id={props.root.backend_id}
          disableTris={props.page?.disableTris}
        />
        {props.page?.components?.filter(isActiveEntry).map((entry) => (
          <EditorialEntry key={entry.sys.id} entry={entry} />
        ))}
        {reviews && reviewsRollup && <ProductReviews reviews={reviews} />}
      </>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const productId = (context.params?.slug as string).split("-").at(-1)!;

  const pageData = await ssrUtils.getServerSideProductPageData(context, context.draftMode, productId);

  if (!pageData) {
    return {
      notFound: true,
    };
  }

  if (pageData.type === "redirect_next_product") {
    let destination = join("/", context.locale!, "/p/", pageData.data.productId as string, "/");

    return {
      redirect: {
        destination,
        permanent: false, // TODO: make product redirects permanent
      },
    };
  }

  if (pageData.type === "redirect_category") {
    let destination = join("/", context.locale!, "/c/", pageData.data.categorySlug, "/");

    return {
      redirect: {
        destination,
        permanent: false,
      },
    };
  }

  if (!context.draftMode) {
    context.res.setHeader("Cache-Control", "public, s-maxage=10, stale-while-revalidate=59");
  }

  return {
    props: {
      ...pageData,
    },
  };
}
